package com.schoolconnect.app.feature.itusers

import com.schoolconnect.app.datasource.network.request.AppStaffUpdateRequest
import com.schoolconnect.app.datasource.network.request.UserCreateRequest
import com.schoolconnect.app.datasource.network.request.UserSchoolRoleUpdateRequest
import com.schoolconnect.app.datasource.network.request.UserUpdateRequest
import com.schoolconnect.app.datasource.network.response.PlatformUserResponse
import com.schoolconnect.app.designsystem.appDateInputToApi
import com.schoolconnect.app.designsystem.AppToastCenter
import com.schoolconnect.app.feature.common.DebouncedActionRunner
import com.schoolconnect.app.feature.common.ServerPagingState
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.feature.common.ServerSortOrder
import com.schoolconnect.app.feature.common.offset
import com.schoolconnect.app.feature.common.resolveServerPageTotal
import com.schoolconnect.app.feature.common.toDateComparable
import com.schoolconnect.app.feature.common.toWindow
import com.schoolconnect.app.feature.common.withPageIndex
import com.schoolconnect.app.feature.common.withPageSize
import com.schoolconnect.app.feature.common.withTotals
import com.schoolconnect.app.repository.itusers.ITUsersRepository
import com.schoolconnect.app.repository.role.RoleLookupRepository
import com.schoolconnect.app.repository.user.UserRepository
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlin.time.Clock

class ITUsersViewModel {
    private companion object {
        const val TAG = "[DATA_RETRIEVE_SC] ITUsersViewModel"
    }

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val usersReloadDebouncer = DebouncedActionRunner(scope)
    private var loadJob: Job? = null

    private val _uiState = MutableStateFlow(
        ITUsersUiState(
            isLoading = true,
            isAnalyticsLoading = false,
            paging = ServerPagingState()
        )
    )

    val uiState: StateFlow<ITUsersUiState> = _uiState.asStateFlow()

    init {
        scheduleUsersReload(immediate = true)
    }

    fun onEvent(event: ITUsersUiEvent) {
        when (event) {
            ITUsersUiEvent.BackClicked -> Unit
            ITUsersUiEvent.RefreshRequested -> {
                refreshItUsers(forceRemote = true)
            }
            ITUsersUiEvent.AddUserClicked -> _uiState.update {
                it.copy(
                    isAddUserModalOpen = true,
                    isAddUserSaving = false,
                    isEditUserSaving = false,
                    errorMessage = "",
                    infoMessage = ""
                )
            }
            ITUsersUiEvent.AddUserModalClosed -> _uiState.update {
                it.copy(
                    isAddUserModalOpen = false,
                    isAddUserSaving = false,
                    errorMessage = ""
                )
            }
            is ITUsersUiEvent.SearchChanged -> updateFilters(
                searchQuery = event.query,
                resetPage = true
            )
            is ITUsersUiEvent.SortChanged -> {
                _uiState.update {
                    val nextAscending = if (it.sortField == event.field) !it.sortAscending else true
                    it.copy(
                        sortField = event.field,
                        sortAscending = nextAscending,
                        paging = it.paging.withPageIndex(0)
                    )
                }
                refreshItUsers(forceRemote = true)
            }
            is ITUsersUiEvent.PageChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageIndex(event.pageIndex)) }
                refreshItUsers(forceRemote = false)
            }
            is ITUsersUiEvent.PageSizeChanged -> {
                _uiState.update { it.copy(paging = it.paging.withPageSize(event.pageSize)) }
                refreshItUsers(forceRemote = false)
            }
            is ITUsersUiEvent.FiltersApplied -> updateFilters(
                searchQuery = event.values.searchQuery,
                roleFilter = event.values.role,
                statusFilter = event.values.status,
                createdFrom = event.values.createdFrom,
                createdTo = event.values.createdTo,
                resetPage = true
            )
            ITUsersUiEvent.FiltersReset -> {
                _uiState.update {
                    it.copy(
                        searchQuery = "",
                        roleFilter = "",
                        statusFilter = "",
                        createdFrom = "",
                        createdTo = "",
                        paging = it.paging.withPageIndex(0),
                        errorMessage = "",
                        infoMessage = "Filters cleared"
                    )
                }
                scheduleUsersReload(immediate = true)
            }
            is ITUsersUiEvent.ToggleStatus -> toggleStatus(event.userId)
            is ITUsersUiEvent.DeleteUser -> deleteUser(event.userId)
            is ITUsersUiEvent.SaveUser -> {
                if (!_uiState.value.isAddUserSaving && !_uiState.value.isEditUserSaving) {
                    saveUser(event.userId, event.values)
                }
            }
            ITUsersUiEvent.ClearMessages -> _uiState.update {
                it.copy(errorMessage = "", infoMessage = "")
            }
        }
    }

    private fun updateFilters(
        searchQuery: String = _uiState.value.searchQuery,
        roleFilter: String = _uiState.value.roleFilter,
        statusFilter: String = _uiState.value.statusFilter,
        createdFrom: String = _uiState.value.createdFrom,
        createdTo: String = _uiState.value.createdTo,
        resetPage: Boolean
    ) {
        _uiState.update {
            it.copy(
                searchQuery = searchQuery,
                roleFilter = roleFilter,
                statusFilter = statusFilter,
                createdFrom = createdFrom,
                createdTo = createdTo,
                paging = if (resetPage) it.paging.withPageIndex(0) else it.paging,
                errorMessage = "",
                infoMessage = ""
            )
        }
        scheduleUsersReload()
    }

    private fun scheduleUsersReload(immediate: Boolean = false) {
        usersReloadDebouncer.submit(immediate = immediate) {
            refreshItUsers(forceRemote = false)
        }
    }

    private fun refreshItUsers(forceRemote: Boolean) {
        loadJob?.cancel()
        val currentState = _uiState.value
        val paging = currentState.paging

        loadJob = scope.launch {
            val normalizedQuery = currentState.searchQuery.trim()
            val roleIds = resolveRoleIds(currentState.roleFilter)
            val isActive = statusFilterToBoolean(currentState.statusFilter)
            val createdFrom = currentState.createdFrom.toApiDateParam()
            val createdTo = currentState.createdTo.toApiDateParam()

            scLog(
                buildString {
                    append("$TAG refreshItUsers start ")
                    append("q=${normalizedQuery.ifBlank { "<blank>" }} ")
                    append("role=${currentState.roleFilter.ifBlank { "<all>" }} ")
                    append("status=${currentState.statusFilter.ifBlank { "<all>" }} ")
                    append("createdFrom=${currentState.createdFrom.ifBlank { "<blank>" }} ")
                    append("createdTo=${currentState.createdTo.ifBlank { "<blank>" }} ")
                    append("pageIndex=${paging.pageIndex} ")
                    append("pageSize=${paging.pageSize} ")
                    append("offset=${paging.offset()} ")
                    append("forceRemote=$forceRemote")
                }
            )

            _uiState.update {
                it.copy(
                    isLoading = true,
                    errorMessage = "",
                    infoMessage = ""
                )
            }

            val result = ITUsersRepository.loadItUsersPage(
                query = normalizedQuery,
                roleIds = roleIds,
                isActive = isActive,
                createdFrom = createdFrom,
                createdTo = createdTo,
                pageIndex = paging.pageIndex,
                pageSize = paging.pageSize,
                sort = ServerSort(
                    field = currentState.sortField.apiField,
                    order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                forceRemote = forceRemote
            )

            result.fold(
                onSuccess = { response ->
                    val mappedUsers = response.users.orEmpty()
                        .distinctBy { it.id.orEmpty() }
                        .mapNotNull { it.toITUserRow() }

                    val exactTotalCount = resolveServerPageTotal(
                        exactTotal = response.count,
                        paging = paging,
                        currentItemCount = mappedUsers.size,
                        pageOffset = paging.offset()
                    )
                    val nextPaging = paging.withTotals(
                        totalCount = exactTotalCount.totalCount,
                        isTotalCountExact = exactTotalCount.isExact
                    )
                    val window = nextPaging.toWindow(mappedUsers.size)
                    val clampedPageIndex = nextPaging.pageIndex.coerceAtMost(maxOf(0, window.pageCount - 1))

                    if (clampedPageIndex != nextPaging.pageIndex) {
                        _uiState.update { current ->
                            current.copy(
                                paging = current.paging.withPageIndex(clampedPageIndex),
                                isLoading = false
                            )
                        }
                        scope.launch {
                            refreshItUsers(forceRemote = forceRemote)
                        }
                        return@fold
                    }

                    _uiState.update { current ->
                        current.copy(
                            isLoading = false,
                            users = mappedUsers,
                            paging = nextPaging,
                            errorMessage = "",
                            infoMessage = current.infoMessage
                        )
                    }
                    scLog(
                        "$TAG refreshItUsers success count=${mappedUsers.size} totalCount=${nextPaging.totalCount} exact=${nextPaging.isTotalCountExact}"
                    )
                },
                onFailure = { throwable ->
                    if (throwable is CancellationException) {
                        scLog("$TAG refreshItUsers cancelled")
                    } else {
                        scLog("$TAG refreshItUsers failure", throwable)
                        _uiState.update {
                            it.copy(
                                isLoading = false,
                                errorMessage = throwable.message ?: "Failed to load IT users"
                            )
                        }
                    }
                }
            )
        }
    }

    private fun resolveRoleIds(roleFilter: String): List<Int> {
        val role = roleFromLabel(roleFilter)
        val roleIdsByUiRole = RoleLookupRepository.loadCachedRoles()
            .mapNotNull { apiRole ->
                val apiRoleId = apiRole.id ?: return@mapNotNull null
                val uiRole = roleFromLabel(apiRole.roleName.orEmpty()) ?: return@mapNotNull null
                uiRole to apiRoleId
            }
            .toMap()

        return if (role == null) {
            listOf(
                roleIdsByUiRole[ITUserRoleUi.SUPER_ADMIN] ?: IT_ROLE_ID_SUPER_ADMIN,
                roleIdsByUiRole[ITUserRoleUi.IT_ADMIN] ?: IT_ROLE_ID_IT_ADMIN,
                roleIdsByUiRole[ITUserRoleUi.IT_SUPPORT] ?: IT_ROLE_ID_IT_SUPPORT
            ).distinct()
        } else {
            listOf(roleIdsByUiRole[role] ?: itUserRoleId(role))
        }
    }

    private fun statusFilterToBoolean(statusFilter: String): Boolean? = when (statusFromLabel(statusFilter)) {
        ITUserStatusUi.ACTIVE -> true
        ITUserStatusUi.INACTIVE -> false
        null -> null
    }

    private fun saveUser(userId: String?, values: ITUserFormValues) {
        val errors = validateITUser(values)
        if (listOf(errors.name, errors.mobile, errors.email, errors.role, errors.status).any { it.isNotBlank() }) {
            _uiState.update {
                it.copy(errorMessage = "Please fix the highlighted fields.", infoMessage = "")
            }
            return
        }

        val role = roleFromLabel(values.role) ?: return
        val status = statusFromLabel(values.status) ?: return
        val normalizedMobile = sanitizeITUserMobile(values.mobile)
        val nextActive = if (userId.isNullOrBlank()) true else status == ITUserStatusUi.ACTIVE

        _uiState.update {
            it.copy(
                isLoading = true,
                isAddUserSaving = userId.isNullOrBlank(),
                isEditUserSaving = !userId.isNullOrBlank(),
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            try {
                if (userId.isNullOrBlank()) {
                    scLog(
                        "$TAG saveUser create start fullName=${values.name.trim()} role=${role} status=${status} mobile=$normalizedMobile"
                    )
                    UserRepository.createUser(
                        UserCreateRequest(
                            fullName = values.name.trim(),
                            mobile = normalizedMobile,
                            email = values.email.trim().ifBlank { null },
                            roleId = itUserRoleId(role),
                            schoolId = null,
                            isActive = nextActive
                        )
                    ).getOrThrow()
                } else {
                    scLog(
                        "$TAG saveUser update start userId=$userId fullName=${values.name.trim()} role=${role} status=${status} mobile=$normalizedMobile"
                    )
                    val current = _uiState.value.users.firstOrNull { row -> row.id == userId }
                    ITUsersRepository.updateAppStaff(
                        staffId = userId,
                        request = AppStaffUpdateRequest(
                            name = values.name.trim(),
                            isActive = nextActive
                        )
                    ).getOrThrow()

                    val linkedUserId = current?.userId.orEmpty().ifBlank { null }
                    if (linkedUserId != null &&
                        (current?.mobile != normalizedMobile || current.email != values.email.trim())
                    ) {
                        UserRepository.updateUser(
                            userId = linkedUserId,
                            request = UserUpdateRequest(
                                mobile = normalizedMobile,
                                email = values.email.trim().ifBlank { null }
                            )
                        ).getOrThrow()
                    }

                    if (current != null && current.role != role) {
                        val assignmentId = current.assignmentId.ifBlank { loadAssignmentId(userId) }
                        if (assignmentId.isNotBlank()) {
                            UserRepository.updateUserRoleAssignment(
                                assignmentId = assignmentId,
                                request = UserSchoolRoleUpdateRequest(
                                    userId = linkedUserId ?: userId,
                                    roleId = itUserRoleId(role),
                                    schoolId = null,
                                    isActive = nextActive,
                                    preferredRole = true
                                )
                            ).getOrThrow()
                        }
                    }
                }

                refreshUsersAfterMutation(
                    successMessage = if (userId.isNullOrBlank()) {
                        "Created ${values.name.trim()}"
                    } else {
                        "Updated ${values.name.trim()}"
                    },
                    resetPage = false,
                    showSuccessToast = true
                )
            } catch (throwable: Throwable) {
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        isAddUserSaving = false,
                        isEditUserSaving = false,
                        errorMessage = throwable.message ?: "Failed to save IT user"
                    )
                }
            }
        }
    }

    private suspend fun loadAssignmentId(userId: String): String {
        val result = UserRepository.loadAssignmentId(userId)
        return result.getOrElse { throwable ->
            scLog("$TAG loadAssignmentId failed userId=$userId", throwable)
            ""
        }
    }

    private fun PlatformUserResponse.toITUserRow(): ITUserRowUi? {
        val apiRoleId = assignment?.roleId ?: roleId
        val role = itUserRoleFromId(apiRoleId)
            ?: roleFromLabel(assignment?.roleTitle ?: roleTitle.orEmpty())
            ?: roleFromLabel(RoleLookupRepository.getRoleName(apiRoleId).orEmpty())
        val createdLabel = formatApiDate(createdAt.orEmpty())
        val createdRank = toDateComparable(createdLabel) ?: 0
        return ITUserRowUi(
            id = id.orEmpty(),
            userId = userId.orEmpty(),
            assignmentId = assignment?.assignmentId ?: assignmentId.orEmpty(),
            name = name.orEmpty().ifBlank { "Unnamed User" },
            mobile = mobile.orEmpty(),
            email = email.orEmpty(),
            role = role,
            status = if (isActive == true) ITUserStatusUi.ACTIVE else ITUserStatusUi.INACTIVE,
            createdRank = createdRank,
            createdOn = createdLabel,
            updatedOn = createdLabel
        )
    }

    private fun formatApiDate(value: String): String {
        val normalized = value.trim()
        if (normalized.isBlank()) return ""
        val datePart = normalized.substringBefore("T").takeIf { it.length >= 10 }?.take(10) ?: return normalized
        val parts = datePart.split("-")
        if (parts.size != 3) return normalized
        val year = parts[0]
        val month = parts[1]
        val day = parts[2]
        return if (year.length == 4 && month.length == 2 && day.length == 2) {
            "$day-$month-$year"
        } else {
            normalized
        }
    }

    private fun toggleStatus(userId: String) {
        val current = _uiState.value.users.firstOrNull { it.id == userId }
        if (current == null) {
            _uiState.update { it.copy(errorMessage = "User not found") }
            return
        }

        val nextActive = current.status != ITUserStatusUi.ACTIVE
        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            ITUsersRepository.toggleAppStaffStatus(userId, nextActive).fold(
                onSuccess = {
                    refreshUsersAfterMutation(
                        successMessage = if (nextActive) {
                            "Activated ${current.name}"
                        } else {
                            "Inactivated ${current.name}"
                        },
                        resetPage = false,
                        showSuccessToast = true
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to update user status"
                        )
                    }
                }
            )
        }
    }

    private fun deleteUser(userId: String) {
        val current = _uiState.value.users.firstOrNull { it.id == userId }
        if (current == null) {
            _uiState.update { it.copy(errorMessage = "User not found") }
            return
        }

        _uiState.update {
            it.copy(
                isLoading = true,
                errorMessage = "",
                infoMessage = ""
            )
        }

        scope.launch {
            ITUsersRepository.deleteAppStaff(userId).fold(
                onSuccess = {
                    refreshUsersAfterMutation(
                        successMessage = "Deleted ${current.name}",
                        resetPage = true,
                        showSuccessToast = true
                    )
                },
                onFailure = { throwable ->
                    _uiState.update {
                        it.copy(
                            isLoading = false,
                            errorMessage = throwable.message ?: "Failed to delete user"
                        )
                    }
                }
            )
        }
    }

    private fun refreshUsersAfterMutation(
        successMessage: String,
        resetPage: Boolean,
        showSuccessToast: Boolean = false
    ) {
        if (resetPage) {
            _uiState.update { it.copy(paging = it.paging.withPageIndex(0)) }
        }
        ITUsersRepository.invalidateCaches()
        if (showSuccessToast) AppToastCenter.showSuccess(successMessage)
        scope.launch {
            val refreshed = refreshItUsersAndWait(forceRemote = true)
            _uiState.update { state ->
                state.copy(
                    isLoading = false,
                    isAddUserSaving = false,
                    isEditUserSaving = false,
                    editSaveCompletedNonce = if (showSuccessToast && successMessage.startsWith("Updated ")) state.editSaveCompletedNonce + 1 else state.editSaveCompletedNonce,
                    isAddUserModalOpen = false,
                    infoMessage = if (showSuccessToast) "" else if (refreshed) successMessage else state.infoMessage
                )
            }
        }
    }

    private suspend fun refreshItUsersAndWait(forceRemote: Boolean): Boolean {
        val previousJob = loadJob
        previousJob?.cancel()
        val currentState = _uiState.value
        val paging = currentState.paging

        return try {
            val result = ITUsersRepository.loadItUsersPage(
                query = currentState.searchQuery.trim(),
                roleIds = resolveRoleIds(currentState.roleFilter),
                isActive = statusFilterToBoolean(currentState.statusFilter),
                createdFrom = currentState.createdFrom.toApiDateParam(),
                createdTo = currentState.createdTo.toApiDateParam(),
                pageIndex = paging.pageIndex,
                pageSize = paging.pageSize,
                sort = ServerSort(
                    field = currentState.sortField.apiField,
                    order = if (currentState.sortAscending) ServerSortOrder.ASC else ServerSortOrder.DESC
                ),
                forceRemote = forceRemote
            )

            result.fold(
                onSuccess = { response ->
                    val mappedUsers = response.users.orEmpty()
                        .distinctBy { it.id.orEmpty() }
                        .mapNotNull { it.toITUserRow() }
                    val exactTotalCount = resolveServerPageTotal(
                        exactTotal = response.count,
                        paging = paging,
                        currentItemCount = mappedUsers.size,
                        pageOffset = paging.offset()
                    )
                    val nextPaging = paging.withTotals(
                        totalCount = exactTotalCount.totalCount,
                        isTotalCountExact = exactTotalCount.isExact
                    )
                    _uiState.update {
                        it.copy(
                            users = mappedUsers,
                            paging = nextPaging
                        )
                    }
                    true
                },
                onFailure = { false }
            )
        } catch (throwable: Throwable) {
            if (throwable is CancellationException) {
                false
            } else {
                scLog("$TAG refreshItUsersAndWait failure", throwable)
                false
            }
        }
    }

    private fun String.toApiDateParam(): String {
        return appDateInputToApi(this).orEmpty()
    }

    fun clear() {
        usersReloadDebouncer.cancel()
        loadJob?.cancel()
        scope.cancel()
    }
}
